import { Box, TextField, Typography } from '@evaneos/b2b-ui';
import { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useGetDossierData } from '../../../../resources/dossier/queries';
import { DossierId } from '../../../../resources/dossier/types';

import { useSidePanel } from '../../SidePanel/SidePanelContext';

import { useMemoEditor } from './useMemoEditor';

type MemoExpandedEditorProps = {
    dossierId: DossierId;
};

export function MemoExpandedEditor({ dossierId }: MemoExpandedEditorProps) {
    const { isOpen } = useSidePanel();
    const { formatMessage } = useIntl();

    const dossierData = useGetDossierData(dossierId);
    const memo = dossierData?.memo ?? null;

    const { localValue, handleChange, handleFocus, handleBlur } = useMemoEditor({
        dossierId,
        memo,
    });

    const textAreaContainerRef = useRef<HTMLDivElement | null>(null);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const [calculatedMaxRows, setCalculatedMaxRows] = useState<number | undefined>(undefined);
    const labelId = `note-label-${dossierId}`;

    useEffect(() => {
        if (!isOpen) return;

        const DRAWER_ANIMATION_DURATION = 300;
        const timeoutId = setTimeout(() => {
            textAreaRef.current?.focus();
        }, DRAWER_ANIMATION_DURATION);

        return () => clearTimeout(timeoutId);
    }, [isOpen]);

    useEffect(() => {
        const container = textAreaContainerRef.current;
        if (!container) {
            return;
        }

        const LINE_HEIGHT_PX = 20;
        const MIN_ROWS = 3;

        const computeRows = () => {
            const availableHeight = container.clientHeight;
            if (!availableHeight) {
                return;
            }

            const rows = Math.max(MIN_ROWS, Math.floor(availableHeight / LINE_HEIGHT_PX));
            setCalculatedMaxRows(rows);
        };

        computeRows();
        window.addEventListener('resize', computeRows);

        return () => {
            window.removeEventListener('resize', computeRows);
        };
    }, []);

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 3,
            }}
        >
            <Typography variant="h4" sx={{ mb: 3 }} id={labelId}>
                {formatMessage({ id: 'dossier.information.notes' })}
            </Typography>

            <Box
                ref={textAreaContainerRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    mb: 3,
                }}
            >
                <TextField
                    multiline
                    fullWidth
                    value={localValue}
                    minRows={calculatedMaxRows}
                    maxRows={calculatedMaxRows}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={formatMessage({ id: 'form.empty_placeholder' })}
                    inputRef={textAreaRef}
                    slotProps={{
                        htmlInput: {
                            'data-testid': 'note-editor-textarea',
                            'aria-labelledby': labelId,
                            sx: {
                                fontSize: '1.4rem',
                                lineHeight: '2rem',
                            },
                        },
                    }}
                    sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                            alignItems: 'flex-start',
                            backgroundColor: 'background.main',
                            pr: 0,
                        },
                        '& .MuiInputBase-inputMultiline': {
                            resize: 'none',
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
